import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../shell/PageHeader';
import { FilterBar, DataTable, StatusPill } from '../patterns';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import {
  listConsumables,
  Consumable,
  ConsumableType,
} from '../../services/consumableService';
import { formatDate } from '../../dateFormat';

const TYPE_CHIPS: { id: ConsumableType; labelKey: string }[] = [
  { id: 'chemical',   labelKey: 'consumables.catalog.filters.type.chemical' },
  { id: 'ppe',        labelKey: 'consumables.catalog.filters.type.ppe' },
  { id: 'fertilizer', labelKey: 'consumables.catalog.filters.type.fertilizer' },
  { id: 'diesel',     labelKey: 'consumables.catalog.filters.type.diesel' },
];

const ConsumableCatalogPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Consumable[]>([]);
  const [search, setSearch] = useState('');
  const [activeType, setActiveType] = useState<ConsumableType | null>(null);

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('consumables.title'), to: '/consumables/catalog' },
    { label: t('consumables.catalog.title') },
  ]);

  useEffect(() => {
    const params: Parameters<typeof listConsumables>[0] = {};
    if (activeType) params.consumableType = activeType;
    listConsumables(params).then(setRows).catch(() => setRows([]));
  }, [activeType]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter(r => r.productName.toLowerCase().includes(q));
  }, [rows, search]);

  const chips = TYPE_CHIPS.map(c => ({
    id: c.id,
    label: t(c.labelKey),
    active: activeType === c.id,
  }));

  return (
    <div data-testid="consumables-catalog-page">
      <PageHeader
        title={t('consumables.catalog.title')}
        meta={t('consumables.catalog.meta')}
        actions={
          <button
            type="button"
            className="pat-btn pat-btn--primary"
            disabled
            title={t('common.comingSoon', { defaultValue: 'Coming soon' })}
          >
            {t('consumables.catalog.newConsumable')}
          </button>
        }
      />

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        chips={chips}
        onChipClick={(id) =>
          setActiveType((prev) => (prev === id ? null : (id as ConsumableType)))
        }
      />

      <DataTable
        rows={filtered}
        rowKey={(r) => r._id}
        onRowClick={(r) => navigate(`/consumables/catalog/${r._id}`)}
        columns={[
          {
            key: 'productName',
            label: t('consumables.catalog.list.columns.productName'),
            sortable: true,
          },
          {
            key: 'consumableType',
            label: t('consumables.catalog.list.columns.consumableType'),
            render: (r) => r.consumableType,
          },
          {
            key: 'needsReview',
            label: t('consumables.catalog.list.columns.needsReview'),
            render: (r) =>
              r.needsReview ? (
                <StatusPill variant="warn">
                  {t('consumables.catalog.needsReviewBadge')}
                </StatusPill>
              ) : null,
          },
          {
            key: 'updatedAt',
            label: t('consumables.catalog.list.columns.updatedAt'),
            sortable: true,
            render: (r) =>
              r.updatedAt ? formatDate(r.updatedAt) : '',
          },
        ]}
        emptyState={t('consumables.catalog.list.empty')}
        mobileCardRow={(r) => (
          <div>
            <strong>{r.productName}</strong>
            <div style={{ fontSize: 11, color: 'var(--shell-text-muted)' }}>
              {r.consumableType}
              {r.needsReview ? (
                <>
                  {' · '}
                  <span style={{ color: 'var(--bs-warning)' }}>
                    {t('consumables.catalog.needsReviewBadge')}
                  </span>
                </>
              ) : null}
            </div>
          </div>
        )}
      />
    </div>
  );
};

export default ConsumableCatalogPage;
